import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  useGetSurveyQuestionsQuery,
  useSubmitSurveyResponseMutation,
} from '../redux/apiSlice';
import { useSearchParams } from 'react-router-dom';

interface SurveyContextType {
  showSurvey: boolean;
  setShowSurvey: (show: boolean) => void;
  surveyData: any;
  isLoading: boolean;
  submitSurvey: (responses: Record<number, string>) => Promise<void>;
  showSuccessMessage: boolean;
  setShowSuccessMessage: (show: boolean) => void;
}

const SurveyContext = createContext<SurveyContextType | undefined>(undefined);

export const useSurvey = () => {
  const context = useContext(SurveyContext);
  if (!context) {
    throw new Error('useSurvey must be used within a SurveyProvider');
  }
  return context;
};

export const SurveyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();
  const surveyId = Number(searchParams.get('survey'));
  const token = searchParams.get('token');
  const [showSurvey, setShowSurvey] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const { data: surveyData, isLoading } = useGetSurveyQuestionsQuery(surveyId, {
    skip: !surveyId,
  });
  const [submitSurveyResponse] = useSubmitSurveyResponseMutation();

  const submitSurvey = async (responses: Record<number, string>) => {
    try {
      const responseString = Object.entries(responses)
        .map(([questionId, answer]) => {
          const question = surveyData?.questions.find(
            (q) => q.id === Number(questionId),
          );
          return `${question?.question}? ${answer}`;
        })
        .join(', ');

      await submitSurveyResponse({
        survey: surveyId,
        response: responseString,
        token: token || '',
      });

      setShowSurvey(false);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error submitting survey:', error);
    }
  };

  // For now, show survey on load
  useEffect(() => {
    if (surveyData && surveyId) {
      setShowSurvey(true);
    }
  }, [surveyData, surveyId, token]);

  const closeSurvey = (show: boolean) => {
    setShowSuccessMessage(show);
    setShowSurvey(false);
    if (show) {
      return;
    }
    window.location.href = '/courses';
  };
  return (
    <SurveyContext.Provider
      value={{
        showSurvey,
        setShowSurvey,
        surveyData,
        isLoading,
        submitSurvey,
        showSuccessMessage,
        setShowSuccessMessage: closeSurvey,
      }}
    >
      {children}
    </SurveyContext.Provider>
  );
};
